import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import CKEditor5 from 'components/CkEditor5';
import type { AlertColor } from '@mui/material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Switch,
  TextField,
  Typography,
  InputLabel,
  FormControl,
} from '@mui/material';
import strings from 'common/Translation/Translate';
import { BookingAddon } from 'screens/Authenticated/BookingDetail/BookingDetailButtons/EditBooking/BookingAddon';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import axiosInstance from 'utils/axios';
import { routes } from 'utils/constants/routes';
import { URLS } from 'utils/constants/urls';
import { useSelector } from 'react-redux';
import { User } from 'utils/redux/reducer/authentication-slice';
import { getSaBookingFieldLabel } from './saBookingFieldLabels';
import type { SaBookingDetailPayload } from './SuperAdminBookingGuestDetailsSection';
import { isCustomizePackage, normalizeCustomPricePackageName } from 'utils/pricePackage';

const SA_LINE_ITEM_STATUS_OPTIONS = [
  { value: 'CONFIRMED', label: 'Confirmed' },
  { value: 'TENTATIVE', label: 'Tentative' },
  { value: 'WAITLISTED', label: 'Waitlisted' },
  { value: 'ENQUIRY', label: 'Enquiry' },
  { value: 'AWAITING_CONFIRMATION', label: 'Awaiting Confirmation' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'EXPIRED', label: 'Expired' },
];

const BOOKING_CONFIRMATION_OPTIONS = [
  { value: 'VENUE', label: 'Venue' },
  { value: 'BLOCK_PAY', label: 'Block pay' },
  { value: 'FULL_PAY', label: 'Full pay' },
];

type DropdownOpt = { id: number | string; name: string };

type FormOptionsPayload = {
  venues: DropdownOpt[];
  pricePackages: DropdownOpt[];
  layouts: DropdownOpt[];
  assignees: DropdownOpt[];
};

function mergeDropdown(options: DropdownOpt[], currentId: string, fallbackLabel: string): DropdownOpt[] {
  const t = currentId.trim();
  if (!t) return options;
  const has = options.some(o => String(o.id) === t);
  if (has) return options;
  const n = Number(t);
  return [{ id: Number.isFinite(n) ? n : t, name: fallbackLabel || `ID ${t}` }, ...options];
}

function mergeStatusOptions(current: string): typeof SA_LINE_ITEM_STATUS_OPTIONS {
  const c = current.trim();
  if (!c || SA_LINE_ITEM_STATUS_OPTIONS.some(o => o.value === c)) {
    return SA_LINE_ITEM_STATUS_OPTIONS;
  }
  return [{ value: c, label: c }, ...SA_LINE_ITEM_STATUS_OPTIONS];
}

function rawToDateInput(raw: unknown): string {
  if (raw == null) return '';
  if (typeof raw === 'string') {
    const t = raw.trim();
    const m = t.match(/^(\d{4}-\d{2}-\d{2})/);
    if (m) return m[1];
  }
  if (Array.isArray(raw) && raw.length >= 3) {
    const y = Number(raw[0]);
    const mo = Number(raw[1]);
    const d = Number(raw[2]);
    if ([y, mo, d].every(n => Number.isFinite(n))) {
      return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    }
  }
  return '';
}

function strVal(raw: unknown): string {
  if (raw == null) return '';
  return String(raw).trim();
}

function numStr(raw: unknown): string {
  if (raw == null || raw === '') return '';
  if (typeof raw === 'number' && Number.isFinite(raw)) return String(raw);
  return String(raw).trim();
}

function parseLong(s: string): number | null {
  const t = s.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? Math.trunc(n) : null;
}

function parseDouble(s: string): number | null {
  const t = s.trim();
  if (!t) return null;
  const n = parseFloat(t);
  return Number.isFinite(n) ? n : null;
}

function selectedDropdownId(raw: unknown): string {
  if (raw == null) return '';
  if (typeof raw === 'object') {
    const obj = raw as { value?: unknown; id?: unknown };
    if (obj.value != null && obj.value !== '') return String(obj.value);
    if (obj.id != null && obj.id !== '') return String(obj.id);
  }
  return String(raw).trim();
}

type FormState = {
  venueId: string;
  eventSubtype: string;
  placeOfEvent: string;
  addressOfEvent: string;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  preLeadTime: string;
  postLeadTime: string;
  partOfDay: string;
  guaranteedGuests: string;
  expectedGuests: string;
  serviceCharge: string;
  hallRental: string;
  overrideHallRental: boolean;
  pricePerPerson: string;
  overridePricePerPerson: boolean;
  minimumRevenue: string;
  overrideMinimumRevenue: boolean;
  pricePerHour: string;
  overridePricePerHour: boolean;
  hourlyPricing: boolean;
  bookingConfirmationBy: string;
  cancelReason: string;
  requestedChanges: string;
  assigneeId: string;
  layoutId: string;
  additionalComments: string;
  status: string;
  pricePackageId: string;
  customPricePackageName: string;
  pmsStatus: string;
  pmsBookingNumber: string;
  pmsBookingSerial: string;
  taxGroupOnGuestChargeId: string;
  taxGroupOnHallRentalId: string;
  taxGroupOnServiceChargeId: string;
};

function lineItemToForm(li: Record<string, unknown>): FormState {
  return {
    venueId: numStr(li.venueId),
    eventSubtype: strVal(li.eventSubtype),
    placeOfEvent: strVal(li.placeOfEvent),
    addressOfEvent: strVal(li.addressOfEvent),
    startDate: rawToDateInput(li.startDate),
    endDate: rawToDateInput(li.endDate),
    startTime: strVal(li.startTime),
    endTime: strVal(li.endTime),
    preLeadTime: numStr(li.preLeadTime) || '0',
    postLeadTime: numStr(li.postLeadTime) || '0',
    partOfDay: strVal(li.partOfDay),
    guaranteedGuests: numStr(li.guaranteedGuests) || '0',
    expectedGuests: numStr(li.expectedGuests) || '0',
    serviceCharge: numStr(li.serviceCharge) || '0',
    hallRental: numStr(li.hallRental),
    overrideHallRental: li.overrideHallRental === true,
    pricePerPerson: numStr(li.pricePerPerson),
    overridePricePerPerson: li.overridePricePerPerson === true,
    minimumRevenue: numStr(li.minimumRevenue),
    overrideMinimumRevenue: li.overrideMinimumRevenue === true,
    pricePerHour: numStr(li.pricePerHour),
    overridePricePerHour: li.overridePricePerHour === true,
    hourlyPricing: li.hourlyPricing === true,
    bookingConfirmationBy: strVal(li.bookingConfirmationBy) || 'VENUE',
    cancelReason: strVal(li.cancelReason),
    requestedChanges: strVal(li.requestedChanges),
    assigneeId: strVal(li.assigneeId),
    layoutId: strVal(li.layoutId),
    additionalComments: typeof li.additionalComments === 'string' ? li.additionalComments : '',
    status: strVal(li.status) || 'TENTATIVE',
    pricePackageId: strVal(li.pricePackageId),
    customPricePackageName: strVal(li.customPricePackageName),
    pmsStatus: strVal(li.pmsStatus),
    pmsBookingNumber: strVal(li.pmsBookingNumber),
    pmsBookingSerial: strVal(li.pmsBookingSerial),
    taxGroupOnGuestChargeId: '',
    taxGroupOnHallRentalId: '',
    taxGroupOnServiceChargeId: '',
  };
}

/** `bookedAddOns` state shape from {@link BookingAddon} (addOnId = catalog id, id = booked row id). */
function buildPayload(form: FormState, bookedAddOns: any[]): Record<string, unknown> {
  const venueId = parseLong(form.venueId);
  if (venueId == null) {
    throw new Error(strings.sa_booking_li_edit_err_venue_id);
  }
  const preLead = parseInt(form.preLeadTime, 10);
  const postLead = parseInt(form.postLeadTime, 10);
  const g = parseInt(form.guaranteedGuests, 10);
  const e = parseInt(form.expectedGuests, 10);
  if (!Number.isFinite(preLead) || !Number.isFinite(postLead) || !Number.isFinite(g) || !Number.isFinite(e)) {
    throw new Error(strings.sa_booking_li_edit_err_numbers);
  }
  const sc = parseFloat(form.serviceCharge);
  if (!Number.isFinite(sc)) {
    throw new Error(strings.sa_booking_li_edit_err_service_charge);
  }

  const hourly = form.hourlyPricing;
  const pricePackageId = hourly ? null : parseLong(form.pricePackageId);
  const pricePerPerson = hourly ? null : parseDouble(form.pricePerPerson);
  const pricePerHour = parseDouble(form.pricePerHour);

  const bookedAddOnsPayload = bookedAddOns.map(item => {
    const addOnId = parseLong(String(item.addOnId ?? ''));
    if (addOnId == null) {
      throw new Error(strings.sa_booking_li_edit_err_addons);
    }
    const q = parseInt(String(item.quantity ?? 0), 10);
    if (!Number.isFinite(q) || q < 0) {
      throw new Error(strings.sa_booking_li_edit_err_addons);
    }
    if (item.type === 'PER_QUANTITY' && q < 1) {
      throw new Error(strings.sa_booking_li_edit_err_addons);
    }
    const priceVal = item.price === '' || item.price == null ? null : parseDouble(String(item.price));
    const payload: Record<string, unknown> = {
      addOnId,
      quantity: q,
      price: priceVal,
    };
    if (item.id != null && item.id !== '' && Number.isFinite(Number(item.id))) {
      payload.id = Number(item.id);
    }
    return payload;
  });

  return {
    venueId,
    eventSubtype: form.eventSubtype.trim() || null,
    placeOfEvent: form.placeOfEvent.trim() || null,
    addressOfEvent: form.addressOfEvent.trim() || null,
    startDate: form.startDate.trim(),
    endDate: form.endDate.trim(),
    startTime: form.startTime.trim(),
    endTime: form.endTime.trim(),
    preLeadTime: preLead,
    postLeadTime: postLead,
    partOfDay: form.partOfDay.trim() || null,
    guaranteedGuests: g,
    expectedGuests: e,
    serviceCharge: sc,
    hallRental: parseDouble(form.hallRental),
    overrideHallRental: form.overrideHallRental,
    pricePerPerson: pricePerPerson ?? null,
    overridePricePerPerson: hourly ? false : form.overridePricePerPerson,
    minimumRevenue: parseDouble(form.minimumRevenue),
    overrideMinimumRevenue: form.overrideMinimumRevenue,
    pricePerHour: pricePerHour ?? null,
    overridePricePerHour: form.overridePricePerHour,
    hourlyPricing: hourly,
    bookingConfirmationBy: form.bookingConfirmationBy.trim(),
    cancelReason: form.cancelReason.trim() || null,
    assigneeId: parseLong(form.assigneeId),
    layoutId: parseLong(form.layoutId),
    additionalComments: form.additionalComments,
    status: form.status.trim(),
    pricePackageId,
    customPricePackageName: normalizeCustomPricePackageName(form.customPricePackageName),
    bookedAddOns: bookedAddOnsPayload,
    taxGroupOnGuestChargeId: parseLong(form.taxGroupOnGuestChargeId),
    taxGroupOnHallRentalId: parseLong(form.taxGroupOnHallRentalId),
    taxGroupOnServiceChargeId: parseLong(form.taxGroupOnServiceChargeId),
  };
}

const Section: FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Paper elevation={0} sx={{ p: 2.5, mb: 2, border: 1, borderColor: 'divider', borderRadius: 2 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}>
      {title}
    </Typography>
    <Grid container spacing={2}>
      {children}
    </Grid>
  </Paper>
);

const PricingCard: FC<{ children: React.ReactNode }> = ({ children }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 2,
      height: '100%',
      borderRadius: 2,
      bgcolor: theme => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : theme.palette.grey[50]),
    }}>
    {children}
  </Paper>
);

const SuperAdminBookingLineItemEdit: FC = () => {
  const user = useSelector(User) as { role?: { value?: string } } | null;
  const navigate = useNavigate();
  const { orderRef: orderRefParam, lineItemId } = useParams<{ orderRef: string; lineItemId: string }>();
  const orderRef = orderRefParam ? decodeURIComponent(orderRefParam) : '';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [options, setOptions] = useState<FormOptionsPayload | null>(null);
  const [taxGroupOptions, setTaxGroupOptions] = useState<DropdownOpt[]>([]);
  const [venueNameHint, setVenueNameHint] = useState('');
  const [addonsList, setAddonsList] = useState<any[]>([]);
  const [bookedAddOns, setBookedAddOns] = useState<any[]>([]);
  const prevVenueForAddOnsRef = useRef<string | undefined>(undefined);
  const [saveReasonOpen, setSaveReasonOpen] = useState(false);
  const [updateReasonDraft, setUpdateReasonDraft] = useState('');
  const [snack, setSnack] = useState<{ open: boolean; message: string; severity: AlertColor }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const statusOptions = useMemo(() => mergeStatusOptions(form?.status ?? ''), [form?.status]);

  const venueOptions = useMemo(() => {
    if (!options || !form) return [];
    return mergeDropdown(options.venues, form.venueId, venueNameHint || strings.sa_booking_li_edit_unknown_venue);
  }, [options, form, venueNameHint]);

  const packageOptions = useMemo(() => {
    if (!options || !form) return [];
    const pkg = options.pricePackages.find(p => String(p.id) === form.pricePackageId);
    return mergeDropdown(options.pricePackages, form.pricePackageId, pkg?.name ?? '');
  }, [options, form]);

  const showCustomPackageName = useMemo(
    () =>
      Boolean(
        form &&
          isCustomizePackage(
            form.pricePackageId,
            packageOptions.map(o => ({ value: o.id, label: o.name })),
          ),
      ),
    [form, packageOptions],
  );

  const layoutOptions = useMemo(() => {
    if (!options || !form) return [];
    const lo = options.layouts.find(p => String(p.id) === form.layoutId);
    return mergeDropdown(options.layouts, form.layoutId, lo?.name ?? '');
  }, [options, form]);

  const assigneeOptions = useMemo(() => {
    if (!options || !form) return [];
    const u = options.assignees.find(p => String(p.id) === form.assigneeId);
    return mergeDropdown(options.assignees, form.assigneeId, u?.name ?? '');
  }, [options, form]);

  const goBackToLookup = useCallback(() => {
    navigate(routes.saBookingLookup, { state: { restoreOrderRef: orderRef } });
  }, [navigate, orderRef]);

  const load = useCallback(async () => {
    if (!orderRef || !lineItemId) {
      setError(strings.sa_booking_li_edit_err_missing_route);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    prevVenueForAddOnsRef.current = undefined;
    try {
      const [detailRes, optsRes, taxGroupRes] = await Promise.all([
        axiosInstance.get<SaBookingDetailPayload>(URLS.superAdminBookingByOrderRef(orderRef)),
        axiosInstance.get<FormOptionsPayload>(URLS.superAdminBookingLineItemFormOptions(lineItemId)),
        axiosInstance.get(URLS.taxGroupDropdown),
      ]);
      const { data, status } = detailRes;
      if (status !== 200 || !data?.bookingLineItems) {
        setError(strings.sa_booking_li_edit_err_load);
        setLoading(false);
        return;
      }
      const li = data.bookingLineItems.find(x => String(x.id) === String(lineItemId));
      if (!li) {
        setError(strings.sa_booking_li_edit_err_not_found);
        setLoading(false);
        return;
      }
      setVenueNameHint(strVal(li.venueName));
      setForm(lineItemToForm(li));
      setOptions(optsRes.data);

      const taxGroupsFromDropdown = Array.isArray(taxGroupRes.data)
        ? taxGroupRes.data.map((g: any) => ({
            id: g.id ?? g.value,
            name: g.name ?? g.label ?? String(g.id ?? g.value ?? ''),
          }))
        : [];
      setTaxGroupOptions(taxGroupsFromDropdown);

      let masterAddOns: any[] = [];
      let masterTaxGroups: DropdownOpt[] = taxGroupsFromDropdown;
      let selectedGuestTaxGroupId = '';
      let selectedHallTaxGroupId = '';
      let selectedServiceChargeTaxGroupId = '';
      try {
        const m = await axiosInstance.get(URLS.getMasterDataForLineItem(lineItemId));
        if (m.status === 200) {
          if (Array.isArray(m.data?.addOns)) {
            masterAddOns = m.data.addOns;
          }
          if (Array.isArray(m.data?.taxGroups) && m.data.taxGroups.length > 0) {
            masterTaxGroups = m.data.taxGroups.map((g: any) => ({
              id: g.id ?? g.value,
              name: g.name ?? g.label ?? String(g.id ?? g.value ?? ''),
            }));
          }
          selectedGuestTaxGroupId = selectedDropdownId(m.data?.selectedTaxGroupOnGuestCharge);
          selectedHallTaxGroupId = selectedDropdownId(m.data?.selectedTaxGroupOnHallRental);
          selectedServiceChargeTaxGroupId = selectedDropdownId(m.data?.selectedTaxGroupOnServiceCharge);
        }
      } catch {
        /* BBR admin may not have org-scoped line-item master-data access */
      }
      setAddonsList(masterAddOns);
      setBookedAddOns([]);
      setTaxGroupOptions(masterTaxGroups);

      // Update form with master-data tax group selections
      setForm(prev =>
        prev
          ? {
              ...prev,
              taxGroupOnGuestChargeId: selectedGuestTaxGroupId,
              taxGroupOnHallRentalId: selectedHallTaxGroupId,
              taxGroupOnServiceChargeId: selectedServiceChargeTaxGroupId,
            }
          : null,
      );

      if (optsRes.status === 200 && optsRes.data) {
        setOptions(optsRes.data);
      } else {
        setOptions({ venues: [], pricePackages: [], layouts: [], assignees: [] });
      }
    } catch {
      setError(strings.sa_booking_li_edit_err_load);
    } finally {
      setLoading(false);
    }
  }, [orderRef, lineItemId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!form || !lineItemId) return;
    const vid = form.venueId;
    if (prevVenueForAddOnsRef.current === undefined) {
      prevVenueForAddOnsRef.current = vid;
      return;
    }
    if (prevVenueForAddOnsRef.current === vid) return;
    prevVenueForAddOnsRef.current = vid;
    let cancelled = false;
    (async () => {
      try {
        const optsRes = await axiosInstance.get<FormOptionsPayload>(
          URLS.superAdminBookingLineItemFormOptions(lineItemId, vid),
        );
        if (cancelled) return;
        if (optsRes.status === 200 && optsRes.data) {
          setOptions(optsRes.data);
        }
        try {
          const m = await axiosInstance.get(URLS.getMasterDataForLineItem(lineItemId));
          if (cancelled) return;
          if (m.status === 200 && Array.isArray(m.data?.addOns)) {
            setAddonsList(m.data.addOns);
            setBookedAddOns([]);
          }
        } catch {
          /* ignore */
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [form?.venueId, form, lineItemId]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm(prev => {
      if (!prev) return prev;
      const next = { ...prev, [key]: value };
      if (
        key === 'pricePackageId' &&
        !isCustomizePackage(
          value,
          packageOptions.map(o => ({ value: o.id, label: o.name })),
        )
      ) {
        next.customPricePackageName = '';
      }
      return next;
    });
  };

  const openSaveReasonDialog = () => {
    if (!form || !lineItemId) return;
    try {
      buildPayload(form, bookedAddOns);
    } catch (err) {
      setSnack({
        open: true,
        message: err instanceof Error ? err.message : strings.sa_booking_li_edit_err_validation,
        severity: 'error',
      });
      return;
    }
    setUpdateReasonDraft('');
    setSaveReasonOpen(true);
  };

  const closeSaveReasonDialog = () => {
    if (!saving) setSaveReasonOpen(false);
  };

  const confirmSaveWithReason = async () => {
    if (!form || !lineItemId) return;
    const reason = updateReasonDraft.trim();
    if (!reason) {
      setSnack({ open: true, message: strings.sa_booking_li_edit_update_reason_required, severity: 'error' });
      return;
    }
    let payload: Record<string, unknown>;
    try {
      payload = {
        ...buildPayload(form, bookedAddOns),
        customPricePackageName: showCustomPackageName
          ? normalizeCustomPricePackageName(form.customPricePackageName)
          : null,
        updateReason: reason,
      };
    } catch (err) {
      setSnack({
        open: true,
        message: err instanceof Error ? err.message : strings.sa_booking_li_edit_err_validation,
        severity: 'error',
      });
      return;
    }
    setSaving(true);
    try {
      await axiosInstance.patch(URLS.superAdminBookingLineItem(lineItemId), payload);
      setSaveReasonOpen(false);
      setSnack({ open: true, message: strings.sa_booking_li_edit_saved, severity: 'success' });
      setTimeout(() => goBackToLookup(), 600);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        strings.sa_booking_li_edit_err_save;
      setSnack({ open: true, message: String(msg), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (user?.role?.value !== 'ROLE_BBR_ADMIN') {
    return <Navigate to={routes.root} replace />;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !form) {
    return (
      <Box sx={{ width: '100%', px: { xs: 1.5, sm: 2 }, py: 4, boxSizing: 'border-box' }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={goBackToLookup}>
          {strings.sa_booking_li_edit_back_lookup}
        </Button>
      </Box>
    );
  }

  const hourly = form.hourlyPricing;
  const preventNumberInputScroll = (e: React.WheelEvent<HTMLDivElement>) => {
    const target = e.target as HTMLInputElement;
    target.blur();
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', px: { xs: 1.5, sm: 2, md: 3 }, py: 2, boxSizing: 'border-box' }}>
      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snack.message}
        </Alert>
      </Snackbar>

      <Dialog open={saveReasonOpen} onClose={closeSaveReasonDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{strings.sa_booking_li_edit_save_reason_title}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {strings.sa_booking_li_edit_save_reason_body}
          </Typography>
          <TextField
            label={strings.sa_booking_lookup_li_reason_label}
            value={updateReasonDraft}
            onChange={e => setUpdateReasonDraft(e.target.value)}
            fullWidth
            required
            multiline
            minRows={3}
            size="small"
            autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeSaveReasonDialog} disabled={saving}>
            {strings.sa_booking_lookup_li_delete_cancel}
          </Button>
          <Button
            onClick={() => void confirmSaveWithReason()}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : undefined}>
            {strings.common_button_save}
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Button startIcon={<ArrowBackIcon />} onClick={goBackToLookup} variant="outlined">
          {strings.sa_booking_li_edit_back}
        </Button>
        <Typography variant="h5" component="h1" sx={{ flex: 1, minWidth: 200 }}>
          {strings.sa_booking_li_edit_title}
        </Typography>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          disabled={saving}
          onClick={openSaveReasonDialog}>
          {strings.common_button_save}
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {getSaBookingFieldLabel('bookingReference')}: {orderRef} · {getSaBookingFieldLabel('id')}: {lineItemId}
      </Typography>

      <Section title={strings.sa_booking_li_edit_section_venue_schedule_guests}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth size="small" required>
            <InputLabel>{getSaBookingFieldLabel('venueName')}</InputLabel>
            <Select
              label={getSaBookingFieldLabel('venueName')}
              value={form.venueId || ''}
              onChange={e => update('venueId', String(e.target.value))}>
              {venueOptions.map(o => (
                <MenuItem key={String(o.id)} value={String(o.id)}>
                  {o.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label={getSaBookingFieldLabel('placeOfEvent')}
            value={form.placeOfEvent}
            onChange={e => update('placeOfEvent', e.target.value)}
            fullWidth
            size="small"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label={getSaBookingFieldLabel('eventSubtype')}
            value={form.eventSubtype}
            onChange={e => update('eventSubtype', e.target.value)}
            fullWidth
            size="small"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label={getSaBookingFieldLabel('addressOfEvent')}
            value={form.addressOfEvent}
            onChange={e => update('addressOfEvent', e.target.value)}
            fullWidth
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label={getSaBookingFieldLabel('startDate')}
            type="date"
            value={form.startDate}
            onChange={e => update('startDate', e.target.value)}
            fullWidth
            required
            size="small"
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label={getSaBookingFieldLabel('endDate')}
            type="date"
            value={form.endDate}
            onChange={e => update('endDate', e.target.value)}
            fullWidth
            required
            size="small"
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label={getSaBookingFieldLabel('startTime')}
            value={form.startTime}
            onChange={e => update('startTime', e.target.value)}
            fullWidth
            required
            size="small"
            placeholder="HH:mm"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label={getSaBookingFieldLabel('endTime')}
            value={form.endTime}
            onChange={e => update('endTime', e.target.value)}
            fullWidth
            required
            size="small"
            placeholder="HH:mm"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            onWheel={preventNumberInputScroll}
            label={getSaBookingFieldLabel('preLeadTime')}
            value={form.preLeadTime}
            onChange={e => update('preLeadTime', e.target.value)}
            fullWidth
            size="small"  
            type="number"
            helperText={strings.sa_booking_li_edit_hint_minutes}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
          onWheel={preventNumberInputScroll}
            label={getSaBookingFieldLabel('postLeadTime')}
            value={form.postLeadTime}
            onChange={e => update('postLeadTime', e.target.value)}
            fullWidth
            size="small"
            type="number"
            helperText={strings.sa_booking_li_edit_hint_minutes}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label={getSaBookingFieldLabel('guaranteedGuests')}
            value={form.guaranteedGuests}
            onChange={e => update('guaranteedGuests', e.target.value)}
            fullWidth
            required
            size="small"
            type="number"
            onWheel={preventNumberInputScroll}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label={getSaBookingFieldLabel('expectedGuests')}
            value={form.expectedGuests}
            onChange={e => update('expectedGuests', e.target.value)}
            fullWidth
            required
            size="small"
            type="number"
            onWheel={preventNumberInputScroll}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <TextField
            fullWidth
            label={getSaBookingFieldLabel('partOfDay')}
            size="small"
            value={form.partOfDay}
            onChange={e => update('partOfDay', e.target.value as string)}
          />
        </Grid>
      </Section>

      <Section title={strings.sa_booking_li_edit_section_pricing}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={hourly}
                onChange={(_, v) => {
                  update('hourlyPricing', v);
                  if (v) {
                    update('pricePackageId', '');
                  }
                }}
                color="primary"
              />
            }
            label={getSaBookingFieldLabel('hourlyPricing')}
          />
        </Grid>

        {hourly ? (
          <>
            <Grid item xs={12} md={6}>
              <PricingCard>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 600, display: 'block', mb: 1.5 }}>
                  {strings.sa_booking_li_edit_pricing_card_hourly}
                </Typography>
                <FormControlLabel
                  sx={{ display: 'flex', mb: 1 }}
                  control={
                    <Switch
                      checked={form.overridePricePerHour}
                      onChange={(_, v) => update('overridePricePerHour', v)}
                      color="primary"
                    />
                  }
                  label={getSaBookingFieldLabel('overridePricePerHour')}
                />
                <TextField
                  label={getSaBookingFieldLabel('pricePerHour')}
                  value={form.pricePerHour}
                  onChange={e => update('pricePerHour', e.target.value)}
                  size="small"
                  type="number"
                  fullWidth
                  disabled={!form.overridePricePerHour}
                  helperText={!form.overridePricePerHour ? strings.sa_booking_li_edit_pricing_auto_helper : undefined}
                  onWheel={preventNumberInputScroll}
                />
              </PricingCard>
            </Grid>
            <Grid item xs={12} md={6}>
              <PricingCard>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 600, display: 'block', mb: 1.5 }}>
                  {strings.sa_booking_li_edit_pricing_card_hall}
                </Typography>
                <FormControlLabel
                  sx={{ display: 'flex', mb: 1 }}
                  control={
                    <Switch
                      checked={form.overrideHallRental}
                      onChange={(_, v) => update('overrideHallRental', v)}
                      color="primary"
                    />
                  }
                  label={getSaBookingFieldLabel('overrideHallRental')}
                />
                <TextField
                  label={getSaBookingFieldLabel('hallRental')}
                  value={form.hallRental}
                  onChange={e => update('hallRental', e.target.value)}
                  size="small"
                  type="number"
                  fullWidth
                  disabled={!form.overrideHallRental}
                  helperText={!form.overrideHallRental ? strings.sa_booking_li_edit_pricing_auto_helper : undefined}
                  onWheel={preventNumberInputScroll}
                />
              </PricingCard>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={strings.sa_booking_li_edit_service_charge_pct}
                value={form.serviceCharge}
                onChange={e => update('serviceCharge', e.target.value)}
                fullWidth
                required
                size="small"
                type="number"
                onWheel={preventNumberInputScroll}
              />
            </Grid>
          </>
        ) : (
          <>
            <Grid item xs={12} md={4}>
              <PricingCard>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 600, display: 'block', mb: 1.5 }}>
                  {strings.sa_booking_li_edit_pricing_card_hall}
                </Typography>
                <FormControlLabel
                  sx={{ display: 'flex', mb: 1 }}
                  control={
                    <Switch
                      checked={form.overrideHallRental}
                      onChange={(_, v) => update('overrideHallRental', v)}
                      color="primary"
                    />
                  }
                  label={getSaBookingFieldLabel('overrideHallRental')}
                />
                <TextField
                  label={getSaBookingFieldLabel('hallRental')}
                  value={form.hallRental}
                  onChange={e => update('hallRental', e.target.value)}
                  size="small"
                  type="number"
                  onWheel={preventNumberInputScroll}
                  fullWidth
                  disabled={!form.overrideHallRental}
                  helperText={!form.overrideHallRental ? strings.sa_booking_li_edit_pricing_auto_helper : undefined}
                />
              </PricingCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <PricingCard>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 600, display: 'block', mb: 1.5 }}>
                  {strings.sa_booking_li_edit_pricing_card_minimum}
                </Typography>
                <FormControlLabel
                  sx={{ display: 'flex', mb: 1 }}
                  control={
                    <Switch
                      checked={form.overrideMinimumRevenue}
                      onChange={(_, v) => update('overrideMinimumRevenue', v)}
                      color="primary"
                    />
                  }
                  label={getSaBookingFieldLabel('overrideMinimumRevenue')}
                />
                <TextField
                  label={getSaBookingFieldLabel('minimumRevenue')}
                  value={form.minimumRevenue}
                  onChange={e => update('minimumRevenue', e.target.value)}
                  size="small"
                  type="number"
                  onWheel={preventNumberInputScroll}
                  fullWidth
                  disabled={!form.overrideMinimumRevenue}
                  helperText={!form.overrideMinimumRevenue ? strings.sa_booking_li_edit_pricing_auto_helper : undefined}
                />
              </PricingCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <PricingCard>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 600, display: 'block', mb: 1.5 }}>
                  {strings.sa_booking_li_edit_pricing_card_ppp}
                </Typography>
                <FormControlLabel
                  sx={{ display: 'flex', mb: 1 }}
                  control={
                    <Switch
                      checked={form.overridePricePerPerson}
                      onChange={(_, v) => update('overridePricePerPerson', v)}
                      color="primary"
                    />
                  }
                  label={getSaBookingFieldLabel('overridePricePerPerson')}
                />
                <TextField
                  label={getSaBookingFieldLabel('pricePerPerson')}
                  value={form.pricePerPerson}
                  onChange={e => update('pricePerPerson', e.target.value)}
                  size="small"
                  type="number"
                  onWheel={preventNumberInputScroll}
                  fullWidth
                  disabled={!form.overridePricePerPerson}
                  helperText={!form.overridePricePerPerson ? strings.sa_booking_li_edit_pricing_auto_helper : undefined}
                />
              </PricingCard>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>{getSaBookingFieldLabel('pricePackageId')}</InputLabel>
                <Select
                  label={getSaBookingFieldLabel('pricePackageId')}
                  value={form.pricePackageId || ''}
                  onChange={e => update('pricePackageId', String(e.target.value))}>
                  <MenuItem value="">
                    <em>—</em>
                  </MenuItem>
                  {packageOptions.map(o => (
                    <MenuItem key={String(o.id)} value={String(o.id)}>
                      {o.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {showCustomPackageName && (
              <Grid item xs={12} md={6}>
                <TextField
                  label={strings.new_booking_custom_package_name}
                  value={form.customPricePackageName}
                  onChange={e => update('customPricePackageName', e.target.value)}
                  fullWidth
                  size="small"
                />
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <TextField
                label={strings.sa_booking_li_edit_service_charge_pct}
                value={form.serviceCharge}
                onChange={e => update('serviceCharge', e.target.value)}
                fullWidth
                required
                size="small"
                type="number"
                onWheel={preventNumberInputScroll}
              />
            </Grid>
          </>
        )}
      </Section>

      <Section title={'Tax Configuration'}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>{strings.venue_text_taxGroupOnGuestCharge}</InputLabel>
            <Select
              label={strings.venue_text_taxGroupOnGuestCharge}
              value={form.taxGroupOnGuestChargeId || ''}
              onChange={e => update('taxGroupOnGuestChargeId', String(e.target.value))}
              disabled={saving}>
              <MenuItem value="">
                <em>—</em>
              </MenuItem>
              {taxGroupOptions.map(o => (
                <MenuItem key={String(o.id)} value={String(o.id)}>
                  {o.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>{strings.venue_text_taxGroupOnHallRental}</InputLabel>
            <Select
              label={strings.venue_text_taxGroupOnHallRental}
              value={form.taxGroupOnHallRentalId || ''}
              onChange={e => update('taxGroupOnHallRentalId', String(e.target.value))}
              disabled={saving}>
              <MenuItem value="">
                <em>—</em>
              </MenuItem>
              {taxGroupOptions.map(o => (
                <MenuItem key={String(o.id)} value={String(o.id)}>
                  {o.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>{strings.venue_text_taxGroupOnServiceCharge}</InputLabel>
            <Select
              label={strings.venue_text_taxGroupOnServiceCharge}
              value={form.taxGroupOnServiceChargeId || ''}
              onChange={e => update('taxGroupOnServiceChargeId', String(e.target.value))}
              disabled={saving}>
              <MenuItem value="">
                <em>—</em>
              </MenuItem>
              {taxGroupOptions.map(o => (
                <MenuItem key={String(o.id)} value={String(o.id)}>
                  {o.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Section>

      <Section title={strings.sa_booking_li_edit_section_status}>
        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth size="small" required>
            <InputLabel>{getSaBookingFieldLabel('status')}</InputLabel>
            <Select
              label={getSaBookingFieldLabel('status')}
              value={form.status}
              onChange={e => update('status', e.target.value as string)}>
              {statusOptions.map(o => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth size="small" required>
            <InputLabel>{getSaBookingFieldLabel('bookingConfirmationBy')}</InputLabel>
            <Select
              label={getSaBookingFieldLabel('bookingConfirmationBy')}
              value={form.bookingConfirmationBy}
              onChange={e => update('bookingConfirmationBy', e.target.value as string)}>
              {BOOKING_CONFIRMATION_OPTIONS.map(o => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>{getSaBookingFieldLabel('assigneeId')}</InputLabel>
            <Select
              label={getSaBookingFieldLabel('assigneeId')}
              value={form.assigneeId || ''}
              onChange={e => update('assigneeId', String(e.target.value))}>
              <MenuItem value="">
                <em>—</em>
              </MenuItem>
              {assigneeOptions.map(o => (
                <MenuItem key={String(o.id)} value={String(o.id)}>
                  {o.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>{strings.sa_booking_li_edit_seating_style}</InputLabel>
            <Select
              label={strings.sa_booking_li_edit_seating_style}
              value={form.layoutId || ''}
              onChange={e => update('layoutId', String(e.target.value))}>
              <MenuItem value="">
                <em>—</em>
              </MenuItem>
              {layoutOptions.map(o => (
                <MenuItem key={String(o.id)} value={String(o.id)}>
                  {o.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label={getSaBookingFieldLabel('cancelReason')}
            value={form.cancelReason}
            onChange={e => update('cancelReason', e.target.value)}
            fullWidth
            multiline
            minRows={2}
            size="small"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label={getSaBookingFieldLabel('requestedChanges')}
            value={form.requestedChanges}
            fullWidth
            multiline
            minRows={2}
            size="small"
            InputProps={{ readOnly: true }}
            sx={{
              '& .MuiInputBase-input': { cursor: 'default' },
              '& .MuiOutlinedInput-root': {
                bgcolor: theme => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : theme.palette.grey[50]),
              },
            }}
          />
        </Grid>
      </Section>

      <Section title={strings.sa_booking_li_edit_section_addons}>
        <Grid item xs={12}>
          {addonsList.length > 0 ? (
            <Box key={`sa-li-addons-${lineItemId}-${form.venueId}`}>
              <BookingAddon addonsDropdown={addonsList} bookedAddOns={bookedAddOns} setBookedAddOns={setBookedAddOns} />
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {strings.sa_booking_li_edit_addons_none}
            </Typography>
          )}
        </Grid>
      </Section>

      <Section title={strings.sa_booking_li_edit_section_pms}>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            label={getSaBookingFieldLabel('pmsStatus')}
            value={form.pmsStatus}
            fullWidth
            size="small"
            InputProps={{ readOnly: true }}
            sx={{
              '& .MuiInputBase-input': { cursor: 'default' },
              '& .MuiOutlinedInput-root': {
                bgcolor: theme => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : theme.palette.grey[50]),
              },
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            label={getSaBookingFieldLabel('pmsBookingNumber')}
            value={form.pmsBookingNumber}
            fullWidth
            size="small"
            InputProps={{ readOnly: true }}
            sx={{
              '& .MuiInputBase-input': { cursor: 'default' },
              '& .MuiOutlinedInput-root': {
                bgcolor: theme => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : theme.palette.grey[50]),
              },
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            label={getSaBookingFieldLabel('pmsBookingSerial')}
            value={form.pmsBookingSerial}
            fullWidth
            size="small"
            InputProps={{ readOnly: true }}
            sx={{
              '& .MuiInputBase-input': { cursor: 'default' },
              '& .MuiOutlinedInput-root': {
                bgcolor: theme => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : theme.palette.grey[50]),
              },
            }}
          />
        </Grid>
      </Section>

      <Section title={getSaBookingFieldLabel('additionalComments')}>
        <Grid item xs={12}>
          <Box
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              overflow: 'hidden',
              '& .ck.ck-editor__editable_inline': { minHeight: '200px !important' },
            }}>
            <CKEditor5
              label=""
              value={form.additionalComments}
              textChange={html => update('additionalComments', html)}
            />
          </Box>
        </Grid>
      </Section>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
        <Button variant="outlined" onClick={goBackToLookup}>
          {strings.cancel_text}
        </Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          disabled={saving}
          onClick={openSaveReasonDialog}>
          {strings.common_button_save}
        </Button>
      </Box>
    </Box>
  );
};

export default SuperAdminBookingLineItemEdit;
